import {
  Flex,
  InputProps,
  Modal,
  Popover,
  Switch,
  Table,
  type TableColumnType,
  Tooltip,
  message,
} from 'antd';
import { Key } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAdminDocumentController_bulkUpdate } from '@api-client/generated/AdminDocumentController/bulkUpdate';
import { useAdminDocumentController_findAll } from '@api-client/generated/AdminDocumentController/findAll';
import { useAdminDocumentController_retryBobSync } from '@api-client/generated/AdminDocumentController/retryBobSync';
import { useAdminDocumentController_runRecognition } from '@api-client/generated/AdminDocumentController/runRecognition';
import { useAdminDocumentController_updateOneById } from '@api-client/generated/AdminDocumentController/updateOneById';
import { Schemas } from '@api-client/generated/types';
import {
  IconArrowDown,
  IconClose,
  IconDownload,
  IconEdit,
  IconMoreVertical,
} from '@assets';
import { Select } from '@components';
import { statusesList, typesList } from '@constants';
import {
  DocumentsStatusTag,
  DownloadDocumentsModal,
  FilterConstructor,
  SendRequestModal,
} from '@entities';
import { useCurrentCompanyId, useInfiniteScrollByPage } from '@hooks';
import { captureException } from '@sentry/react';
import {
  getBulkMarkAsBookedEligibility,
  getDocumentExpenseDetailsPath,
  getMarkAsBookedEligibility,
  getNextPage,
  getRecognitionEligibility,
  getRetryEligibility,
  getSearchParams,
  paginationNormalizer,
} from '@utils';

import { NameCell } from './NameCell';
import * as S from './styled';

type DocumentFile = Schemas.Document;

const dateByOptions = [
  { label: 'Issue date', value: 'issueDate' },
  { label: 'Upload date', value: 'createdAt' },
];

type FilterOptions = Record<string, unknown>;

type DocumentsContentProps = {
  title?: ReactNode;
  withSearchParams?: boolean;
  paginationCurrentPage?: number;
  externalCompanyId?: string;
  onPagination?: (metaData: Schemas.PaginationMetadata) => void;
  onFilter?: (options: FilterOptions) => void;
};

const DocumentsContent: FC<DocumentsContentProps> = ({
  title,
  withSearchParams = true,
  paginationCurrentPage,
  externalCompanyId,
  onPagination,
  onFilter,
}) => {
  const accountCompanyId = useCurrentCompanyId();

  const companyId = externalCompanyId || accountCompanyId;

  const { search } = useLocation();
  const navigate = useNavigate();

  const params = getSearchParams<{ [key: string]: string }>(search);

  const [files, setFiles] = useState<DocumentFile[]>([]);
  const [isLastPage, setIsLastPage] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const [actionVisibleId, setActionVisibleId] = useState('');
  const [isOpenRequestModal, setIsOpenRequestModal] = useState(false);
  const [isOptionsPopoverOpen, setIsOptionsPopoverOpen] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);

  const [filterOptions, setFilterOptions] = useState<FilterOptions>({});

  // Sort field and date-mode / no-issue-date are driven from the table headers
  // and the "Date mode" dropdown rather than plain filter controls, so they
  // live here and are pushed into the filter query via `externalParameters`.
  const [sortBy, setSortBy] = useState<string | undefined>(params?.sortBy);
  const [filterDateBy, setFilterDateBy] = useState<string>(
    params?.filterDateBy || 'issueDate'
  );
  const [blankIssueDate, setBlankIssueDate] = useState<boolean>(
    !!params?.blankIssueDate
  );
  // A facet, not a status: it narrows whatever status bucket is selected rather than replacing it,
  // which is the whole point of the backend's `withErrors` [ENG-1256].
  const [withErrors, setWithErrors] = useState<boolean>(!!params?.withErrors);

  const filterExternalParams = useMemo(
    () => ({
      sortBy: sortBy || undefined,
      // Only surface the non-default mode in the URL/query; issue-date is the
      // implicit default (see `documentListQuery`), so keep the URL clean.
      // Passing `undefined` also clears a previously-set value on switch-back.
      filterDateBy: filterDateBy === 'issueDate' ? undefined : filterDateBy,
      blankIssueDate: blankIssueDate || undefined,
      withErrors: withErrors || undefined,
    }),
    [sortBy, filterDateBy, blankIssueDate, withErrors]
  );

  // Single-direction sort (the API's `sortBy` has no asc/desc); clicking the
  // active field again clears the sort.
  const handleSort = (field: string) =>
    setSortBy((prev) => (prev === field ? undefined : field));

  const handleChangeDateMode = (mode: string) => {
    setFilterDateBy(mode);
    // Issue-date mode is incompatible with the "no issue date" filter.
    if (mode === 'issueDate') {
      setBlankIssueDate(false);
    }
  };

  const handleToggleBlankIssueDate = (checked: boolean) => {
    setBlankIssueDate(checked);
    // Documents without an issue date can only be filtered by upload date.
    if (checked) {
      setFilterDateBy('createdAt');
    }
  };

  const [modal, contextHolder] = Modal.useModal();

  const { currentPage: currentPageByScroll, resetScroll } =
    useInfiniteScrollByPage({ isLastPage });

  // Filters + sort without `page`, so the details page can fetch further pages
  // of ids on demand for prev/next navigation across the filtered list.
  const documentListQuery = {
    companyId: companyId!,
    ignoreDrafts: true,
    filterDateBy: 'issueDate' as const,
    ...filterOptions,
  };

  const {
    data,
    isPending: loading,
    refetch,
  } = useAdminDocumentController_findAll({
    params: {
      ...documentListQuery,
      page: paginationCurrentPage || currentPageByScroll,
    },
  });

  // Derive the next-page cursor from the ids actually loaded into `files`
  // (not from `data`, which can be one page ahead of `files` before the append
  // effect runs), so the cursor passed to the details page never skips a page.
  const loadedDocumentsPages = data?.metadata.perPage
    ? Math.ceil(files.length / data.metadata.perPage)
    : 0;
  const nextDocumentsPage = data
    ? getNextPage(loadedDocumentsPages, data.metadata.totalPages)
    : null;

  useEffect(
    () => {
      if (data) {
        if (paginationCurrentPage) {
          onPagination?.(data.metadata);
          onFilter?.(filterOptions);

          setFiles(data.records);
        } else {
          const normalizeTransactions = paginationNormalizer<DocumentFile>({
            prevData: files,
            scrollPage: currentPageByScroll,
            setCurrentPage,
            currentPage,
            records: data.records,
            metadata: data.metadata,
          });

          if (data.metadata.currentPage === data.metadata.totalPages) {
            setIsLastPage(true);
          }

          if (normalizeTransactions) {
            setFiles(normalizeTransactions);
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  );

  const { mutate: updateFile } = useAdminDocumentController_updateOneById();
  const { mutate: retryBobSync } = useAdminDocumentController_retryBobSync();
  const { mutate: updateFiles } = useAdminDocumentController_bulkUpdate();
  const { mutate: recognizeFile } = useAdminDocumentController_runRecognition();

  const handleUpdateFile = <K extends keyof Schemas.AdminUpdateDocumentDto>(
    id: string,
    key: K,
    value: Schemas.AdminUpdateDocumentDto[K],
    withMessage: boolean = true
  ) => {
    updateFile(
      {
        parameter: {
          id: id!,
          companyId: companyId!,
        },
        requestBody: {
          [key]: value,
        },
      },
      {
        onSuccess: () => {
          // Keep the parent `files` list in sync so row consumers reading
          // `record.name`/`record.type` (e.g. the download link) don't go stale
          // until the next refetch.
          setFiles((prev) =>
            prev.map((file) =>
              file.id === id ? { ...file, [key]: value } : file
            )
          );
          withMessage &&
            message.open({
              type: 'success',
              content: 'File information has been successfully updated',
            });
        },
        onError: (error) => {
          captureException(error);
          message.error('Failed to update the file. Please try again.');
        },
      }
    );
  };

  const handleReport = (code?: string) => {
    updateFile(
      {
        parameter: {
          companyId: companyId!,
          id: actionVisibleId,
        },
        requestBody: {
          errorCode: code,
          adminStatus: 'requested_changes',
        },
      },
      {
        onSuccess: () => {
          setIsOpenRequestModal(false);
          setActionVisibleId('');
          refetch();
          message.success('Report was sent');
        },
      }
    );
  };

  const handleConfirmBooked = () => {
    modal.confirm({
      title: 'Are you sure you want to mark as booked this file?',
      okButtonProps: {
        size: 'large',
      },
      cancelButtonProps: {
        size: 'large',
      },
      icon: null,
      onOk: () => {
        handleUpdateFile(actionVisibleId, 'adminStatus', 'processed');
        setFiles((prev) =>
          prev.map((file) =>
            file.id === actionVisibleId
              ? { ...file, adminStatus: 'processed' }
              : file
          )
        );
        setActionVisibleId('');
      },
      onCancel: () => {
        setActionVisibleId('');
      },
    });
  };

  const handleConfirmBookedFiles = () => {
    modal.confirm({
      title: 'Are you sure you want to mark as booked these files?',
      okButtonProps: {
        size: 'large',
      },
      cancelButtonProps: {
        size: 'large',
      },
      icon: null,
      onOk: handleBookFiles,
      onCancel: () => {
        setActionVisibleId('');
      },
    });
  };

  const handleConfirmRecognizeFile = () => {
    modal.confirm({
      title: 'Are you sure you want to recognize this file?',
      okButtonProps: {
        size: 'large',
      },
      cancelButtonProps: {
        size: 'large',
      },
      icon: null,
      onOk: () => {
        handleFileRecognize(actionVisibleId);
        setActionVisibleId('');
      },
      onCancel: () => {
        setActionVisibleId('');
      },
    });
  };

  // const onReport = () => {
  //   setIsOpenRequestModal(true);
  //   setIsOptionsPopoverOpen(false);
  // };

  const onMarkAsBooked = () => {
    handleConfirmBooked();
    setIsOptionsPopoverOpen(false);
  };

  const onRetryBobSync = (record: DocumentFile) =>
    modal.confirm({
      title: 'Retry the BOB sync?',
      content:
        'The document goes back into the BOB queue with its error cleared and the stuck timer restarted. Nothing is sent to BOB50 until the connector picks it up.',
      okButtonProps: { size: 'large' },
      cancelButtonProps: { size: 'large' },
      icon: null,
      onOk: () =>
        retryBobSync(
          { parameter: { companyId: companyId!, id: record.id } },
          {
            onSuccess: () => {
              message.success('The document is back in the BOB queue');
              setActionVisibleId('');
              setIsOptionsPopoverOpen(false);
              refetch();
            },
            // 409 ALREADY_BOOKED is the case the disabled state covers; reaching it means the row moved
            // under us, and saying nothing would look like a retry that worked.
            onError: (error) => {
              captureException(error);
              message.error(
                'Could not retry the BOB sync. Reload the list and check the Doc ID.'
              );
            },
          }
        ),
    });

  const onRestartRecognition = () => {
    handleConfirmRecognizeFile();
    setIsOptionsPopoverOpen(false);
  };

  const columns: TableColumnType<DocumentFile>[] = [
    {
      key: 'adminStatus',
      dataIndex: 'adminStatus',
      width: 160,
      title: (
        <SortHeader
          label="Status"
          field="status"
          active={sortBy}
          onSort={handleSort}
        />
      ),
      render: (status, record) => (
        <DocumentsStatusTag
          status={status}
          bobStatus={record.bobStatus}
          hasError={record.hasError}
          errorCode={record.errorCode}
          bobPendingAt={record.bobPendingAt}
        />
      ),
    },
    {
      key: 'name',
      dataIndex: 'name',
      title: 'Name',
      render: (name, record) => (
        <NameCell
          id={record.id}
          name={name}
          hasAmount={!!record.documentMetadata?.amount}
          onRename={(id, value) => handleUpdateFile(id, 'name', value)}
        />
      ),
      onCell: ({ id }) => ({
        onClick: () => {
          navigate(getDocumentExpenseDetailsPath(companyId!, id), {
            state: {
              fromSearch: search,
              // Ordered, filtered ids so the details page can offer prev/next
              // navigation across the list the operator is currently viewing.
              documentIds: files.map((file) => file.id),
              // Forward paging only makes sense for the accumulating
              // infinite-scroll list. In controlled-pagination mode (modal),
              // `files` is a single replaced page, so the cursor would skip the
              // other pages — omit it and keep prev/next within the loaded ids.
              ...(paginationCurrentPage
                ? {}
                : {
                    documentQuery: documentListQuery,
                    nextPage: nextDocumentsPage,
                  }),
            },
          });
        },
      }),
      width: 200,
      ellipsis: true,
    },
    {
      key: 'invoiceNumber',
      dataIndex: 'bookeepingMetadata',
      title: 'Invoice number',
      width: 160,
      render: (metadata: Schemas.BookingDocumentMetadata) => (
        <span>{metadata?.number}</span>
      ),
      ellipsis: true,
    },
    {
      key: 'contactName',
      dataIndex: 'bobContact',
      title: 'Contact',
      render: (bobContact: Schemas.BobContact) => (
        <span>{bobContact?.name}</span>
      ),
      ellipsis: true,
    },
    {
      key: 'type',
      dataIndex: 'type',
      title: 'Type',
      render: (type, record) => (
        <TypeSelect
          id={record.id}
          type={type}
          onChangeType={(id, value) => handleUpdateFile(id, 'type', value)}
        />
      ),
    },
    {
      key: 'referenceId',
      dataIndex: 'referenceId',
      title: 'Bob50 ID',
      // Fixed to ~12 characters plus the edit icon.
      width: 150,
      render: (referenceId, record) => (
        <ReferenceInput
          id={record.id}
          defaultValue={referenceId}
          onUpdateReference={(id, referenceId) =>
            handleUpdateFile(id, 'referenceId', referenceId)
          }
        />
      ),
    },
    {
      key: 'createdAt',
      dataIndex: 'createdAt',
      width: 120,
      title: (
        <SortHeader
          label="Upload date"
          field="createdAt"
          active={sortBy}
          onSort={handleSort}
        />
      ),
      render: (createdAt) => (
        <span>{dayjs(createdAt).format('DD.MM.YYYY')}</span>
      ),
    },
    {
      key: 'issueDate',
      dataIndex: 'bookeepingMetadata',
      title: 'Issue date',
      width: 120,
      render: (metadata) => (
        <span>
          {metadata &&
            metadata.issueDate &&
            dayjs(metadata.issueDate).format('DD.MM.YYYY')}
        </span>
      ),
    },
    {
      key: 'actions',
      render: (_, record) => {
        const retry = getRetryEligibility(record);
        const markAsBooked = getMarkAsBookedEligibility(record);
        const recognition = getRecognitionEligibility(record);

        return (
          <Flex gap={20} align="center">
            <Tooltip title="Download">
              <a
                href={record.url}
                download={record.name}
                target="_blank"
                rel="noreferrer"
                aria-label="Download"
                onClick={() => {
                  if (
                    record.adminStatus !== 'processing' &&
                    record.adminStatus !== 'processed'
                  ) {
                    handleUpdateFile(
                      record.id,
                      'adminStatus',
                      'processing',
                      false
                    );
                    setFiles((prev) =>
                      prev.map((file) =>
                        file.id === record.id
                          ? { ...file, adminStatus: 'processing' }
                          : file
                      )
                    );
                  }
                }}
              >
                <S.DownloadIcon>
                  <IconDownload width={18} height={18} />
                </S.DownloadIcon>
              </a>
            </Tooltip>
            <Popover
              open={actionVisibleId === record.id && isOptionsPopoverOpen}
              onOpenChange={(open) => {
                // Toggle for the clicked row, close when antd requests it (repeat
                // click / outside click).
                setActionVisibleId(open ? record.id : '');
                setIsOptionsPopoverOpen(open);
                setIsOpenRequestModal(false);
              }}
              trigger="click"
              placement="bottomRight"
              arrow={false}
              overlayClassName="ui-popover-legal-actions"
              title={
                <S.Actions>
                  {/*
                    Booking a document that is already booked writes the status it already holds:
                    the backend commits the no-op and the list reports a successful update, so the
                    action is refused here — with its reason — the way the details screen has always
                    refused it.
                  */}
                  <Tooltip title={markAsBooked.blockedReason ?? ''}>
                    <S.PopoverItem
                      isDisabled={!markAsBooked.allowed}
                      aria-disabled={!markAsBooked.allowed}
                      onClick={
                        markAsBooked.allowed ? onMarkAsBooked : undefined
                      }
                    >
                      Mark as Booked
                    </S.PopoverItem>
                  </Tooltip>

                  {retry && (
                    <Tooltip title={retry.blockedReason ?? ''}>
                      <S.PopoverItem
                        isDisabled={!retry.allowed}
                        // `S.PopoverItem` is a styled span, so `isDisabled` is appearance only — without
                        // this a screen reader still offers the action as available.
                        aria-disabled={!retry.allowed}
                        onClick={
                          retry.allowed
                            ? () => onRetryBobSync(record)
                            : undefined
                        }
                      >
                        Retry BOB sync
                      </S.PopoverItem>
                    </Tooltip>
                  )}

                  {/*
                    Recognition is refused both for a document in the BOB queue and for one that is
                    already booked; `getRecognitionEligibility` carries which of the two it is. The
                    queued case the backend rejects silently, so the operator used to click and be
                    told nothing [ENG-1261]; the booked case the backend does not reject at all, so
                    this is the only thing standing between a booked document and an OCR pass over
                    the data its BOB50 booking was made from.
                  */}
                  <Tooltip title={recognition.blockedReason ?? ''}>
                    <S.PopoverItem
                      isDisabled={!recognition.allowed}
                      aria-disabled={!recognition.allowed}
                      onClick={
                        recognition.allowed ? onRestartRecognition : undefined
                      }
                    >
                      Restart recognition
                    </S.PopoverItem>
                  </Tooltip>
                </S.Actions>
              }
            >
              <S.MoreIcon>
                <IconMoreVertical width={18} height={18} />
              </S.MoreIcon>
            </Popover>
          </Flex>
        );
      },
    },
  ];

  useEffect(
    () => {
      resetScroll();
      setIsLastPage(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterOptions]
  );

  // A selection must not outlive the rows it was made on: filtering replaces them, and so does paging
  // in the modal's paginated mode, where `files` holds one page at a time. Kept ids would still be
  // sent by a bulk action while nothing on screen could show them as blocked.
  useEffect(() => {
    setSelectedRowKeys([]);
  }, [filterOptions, paginationCurrentPage]);

  const onSelectChange = (newSelectedRowKeys: Key[]) =>
    setSelectedRowKeys(newSelectedRowKeys);

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    hideSelectAll: true,
    columnWidth: 48,
  };

  const selectedDocuments = useMemo(
    () => files.filter((file) => selectedRowKeys.includes(file.id)),
    [files, selectedRowKeys]
  );

  // The selection is judged against the rows on screen, but `handleBookFiles` sends every id in it, so
  // the two have to describe the same set. The count is passed for the case where they do not: the
  // rule then refuses the batch instead of clearing a hidden document on a subset it never saw.
  const bulkMarkAsBooked = getBulkMarkAsBookedEligibility(
    selectedDocuments,
    selectedRowKeys.length
  );

  const handleBookFiles = () => {
    updateFiles(
      {
        parameter: {
          companyId: companyId!,
        },
        requestBody: {
          ids: selectedRowKeys as string[],
          adminStatus: 'processed',
        },
      },
      {
        onSuccess: () => {
          message.success('Files were updated');
          setSelectedRowKeys([]);
          refetch();
        },
      }
    );
  };

  const handleFileRecognize = (id: string) => {
    recognizeFile(
      {
        parameter: {
          companyId: companyId!,
          id,
        },
      },
      {
        onSuccess: () => {
          message.success('File was recognized');
          refetch();
        },
      }
    );
  };

  const defaultDownloadModalValues = {
    ...(params?.status && {
      status: params?.status,
    }),
    ...(params?.type && {
      type: params?.type,
    }),
    // The export opens on what the list is showing, the error facet included — otherwise an operator
    // looking at the flagged documents downloads all of them.
    ...(withErrors && { withErrors: true }),
    ...(params?.dateFrom &&
      params?.dateTo && {
        period: [dayjs(params?.dateFrom), dayjs(params?.dateTo)],
      }),
    filterDateBy: 'issueDate',
  };

  return (
    <Flex gap={24} vertical>
      {contextHolder}

      {title}

      <Flex justify="space-between" align="center">
        <FilterConstructor<DocumentFile>
          columns={columns}
          wrapControls
          externalParameters={filterExternalParams}
          controls={{
            left: [
              {
                type: 'search',
                formName: 'name',
                params: {
                  placeholder: 'Name',
                  width: 200,
                  allowClear: true,
                  clearIcon: <IconClose width={16} height={16} />,
                },
              },
              {
                type: 'search',
                formName: 'invoiceNumber',
                params: {
                  placeholder: 'Invoice number',
                  width: 200,
                  allowClear: true,
                  clearIcon: <IconClose width={16} height={16} />,
                },
              },
              {
                type: 'search',
                formName: 'contactName',
                params: {
                  placeholder: 'Contact',
                  width: 180,
                  allowClear: true,
                  clearIcon: <IconClose width={16} height={16} />,
                },
              },
              {
                type: 'search',
                formName: 'referenceId',
                params: {
                  placeholder: 'Bob50 ID',
                  width: 160,
                  allowClear: true,
                  clearIcon: <IconClose width={16} height={16} />,
                },
              },
              {
                type: 'divider',
              },
              {
                type: 'custom-component',
                component: (
                  <DateModeControl
                    value={filterDateBy}
                    blankIssueDate={blankIssueDate}
                    onChangeMode={handleChangeDateMode}
                    onToggleBlankIssueDate={handleToggleBlankIssueDate}
                  />
                ),
              },
              {
                type: 'range-picker',
                formName: 'date',
                // Keep the date filter at a fixed size — it should never shrink
                // as other controls wrap.
                params: {
                  style: { width: 240, flex: 'none' },
                },
              },
              {
                type: 'select',
                formName: 'status',
                params: {
                  options: statusesList,
                  placeholder: 'Status',
                  width: 133,
                  allowClear: true,
                },
              },
              {
                type: 'select',
                formName: 'type',
                params: {
                  options: typesList,
                  placeholder: 'Type',
                  width: 150,
                  allowClear: true,
                },
              },
              {
                type: 'custom-component',
                component: (
                  <ErrorsOnlyControl
                    checked={withErrors}
                    onToggle={setWithErrors}
                  />
                ),
              },
            ],
            right: [
              {
                type: 'custom-component',
                component: (
                  <S.DownloadButton
                    size="large"
                    type="text"
                    onClick={() => setIsDownloadModalOpen(true)}
                  >
                    <IconDownload />
                  </S.DownloadButton>
                ),
              },
              {
                type: 'custom-component',
                component: (
                  <Tooltip title={bulkMarkAsBooked.blockedReason ?? ''}>
                    {/* Wrapped in a span, and the button itself made transparent to the pointer while
                        it is disabled: a disabled button receives no mouse events, so without both the
                        tooltip carrying the reason would never open. */}
                    <span>
                      <S.BookedButton
                        size="large"
                        type="default"
                        disabled={
                          !selectedRowKeys?.length || !bulkMarkAsBooked.allowed
                        }
                        onClick={handleConfirmBookedFiles}
                        style={{
                          pointerEvents: bulkMarkAsBooked.allowed
                            ? undefined
                            : 'none',
                        }}
                      >
                        Mark as Booked
                      </S.BookedButton>
                    </span>
                  </Tooltip>
                ),
              },
            ],
          }}
          onChange={setFilterOptions}
          onRequest={refetch}
          withSearchParams={withSearchParams}
        />
      </Flex>

      <S.TableContainer>
        <Table
          rowKey={({ id }) => id}
          rowSelection={rowSelection}
          rowClassName={() => 'ui-table-custom-row'}
          dataSource={files}
          columns={columns}
          loading={loading}
          pagination={false}
          scroll={{ x: 720 }}
        />
      </S.TableContainer>

      <SendRequestModal
        open={!!actionVisibleId && isOpenRequestModal}
        onRequest={handleReport}
        onCancel={() => {
          setIsOpenRequestModal(false);
          setActionVisibleId('');
        }}
      />

      <DownloadDocumentsModal
        page={currentPage}
        open={isDownloadModalOpen}
        onCancel={() => setIsDownloadModalOpen(false)}
        onRefreshList={refetch}
        defaultValues={defaultDownloadModalValues}
      />
    </Flex>
  );
};

// A sortable column header: label + a chevron that is highlighted when this
// field is the active sort and greyed out otherwise. The API's `sortBy` has no
// direction, so this just selects the field (clicking the active one clears it).
const SortHeader: FC<{
  label: string;
  field: string;
  active?: string;
  onSort: (field: string) => void;
}> = ({ label, field, active, onSort }) => (
  <S.SortHeader
    role="button"
    tabIndex={0}
    onClick={() => onSort(field)}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSort(field);
      }
    }}
  >
    {label}
    <S.SortChevron $active={active === field}>
      <IconArrowDown width={12} height={12} />
    </S.SortChevron>
  </S.SortHeader>
);

// "Date mode" picks which date the range filter applies to, and its dropdown
// also hosts the "No issue date" toggle. Enabling that toggle forces the mode
// to upload date (issue-date documents can't be missing an issue date).
const DateModeControl: FC<{
  value: string;
  blankIssueDate: boolean;
  onChangeMode: (mode: string) => void;
  onToggleBlankIssueDate: (checked: boolean) => void;
}> = ({ value, blankIssueDate, onChangeMode, onToggleBlankIssueDate }) => (
  <Flex align="center" gap={8}>
    <S.FilterLabel>Date mode</S.FilterLabel>
    <Select
      size="large"
      width={150}
      value={value}
      options={dateByOptions}
      onChange={(next) => onChangeMode(next as string)}
      dropdownRender={(menu) => (
        <>
          {menu}
          <S.DateModeDivider />
          <S.DateModeSwitchRow onMouseDown={(e) => e.preventDefault()}>
            <span>No issue date</span>
            <Switch
              size="small"
              checked={blankIssueDate}
              onChange={onToggleBlankIssueDate}
            />
          </S.DateModeSwitchRow>
        </>
      )}
    />
  </Flex>
);

// The error facet as a switch: it combines with the status filter instead of being one of its values,
// so a flagged document still answers its own bucket [ENG-1256].
const ErrorsOnlyControl: FC<{
  checked: boolean;
  onToggle: (checked: boolean) => void;
}> = ({ checked, onToggle }) => (
  <Flex align="center" gap={8}>
    <S.FilterLabel>With errors</S.FilterLabel>
    <Switch size="small" checked={checked} onChange={onToggle} />
  </Flex>
);

const ReferenceInput: FC<
  InputProps & {
    onUpdateReference: (id: string, referenceId?: string) => void;
    id: string;
  }
> = ({ onUpdateReference, id, defaultValue, ...rest }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState<string | undefined>(
    defaultValue as string
  );

  useEffect(() => {
    setValue(defaultValue as string);
  }, [defaultValue]);

  const commit = () => {
    setIsEditing(false);
    if (value !== defaultValue) {
      onUpdateReference(id, value);
    }
  };

  // Gated behind the edit button so the value isn't changed by accident.
  if (isEditing) {
    return (
      <S.ReferenceInput
        autoFocus
        style={{ width: '12ch' }}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onPressEnter={commit}
        {...rest}
      />
    );
  }

  return (
    <S.DocumentNameWrapper
      align="center"
      justify={value ? 'flex-start' : 'center'}
    >
      <S.ReferenceText>{value || '—'}</S.ReferenceText>
      <S.EditIcon
        role="button"
        tabIndex={0}
        aria-label="Edit Bob50 ID"
        onClick={() => setIsEditing(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsEditing(true);
          }
        }}
      >
        <IconEdit width={14} height={14} />
      </S.EditIcon>
    </S.DocumentNameWrapper>
  );
};

const TypeSelect: FC<{
  id: string;
  type?: string;
  onChangeType: (id: string, type: string) => void;
}> = ({ id, type, onChangeType }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState<string | undefined>(type || undefined);

  useEffect(() => {
    setValue(type || undefined);
  }, [type]);

  const label = typesList.find((option) => option.value === value)?.label;

  // Frozen by default so scrolling/clicking the row can't change the type by
  // accident. The edit button opens the dropdown; picking a value (or blurring)
  // freezes it back.
  if (isEditing) {
    return (
      <Select
        autoFocus
        defaultOpen
        withoutContainer
        variant="borderless"
        value={value}
        options={typesList}
        placeholder="Type"
        popupMatchSelectWidth={false}
        onChange={(next) => {
          setValue(next as string);
          onChangeType(id, next as string);
          setIsEditing(false);
        }}
        onBlur={() => setIsEditing(false)}
      />
    );
  }

  return (
    <S.DocumentNameWrapper align="center">
      <S.DocumentName>{label || 'No type'}</S.DocumentName>
      <S.EditIcon
        role="button"
        tabIndex={0}
        aria-label="Edit type"
        onClick={() => setIsEditing(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsEditing(true);
          }
        }}
      >
        <IconEdit width={14} height={14} />
      </S.EditIcon>
    </S.DocumentNameWrapper>
  );
};

export default DocumentsContent;
